// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyInterest, TyProject, TyUnit } from "@/types";
import { readLangValue } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import ChoiceInput from "@/UI/inputs/ChoiceInput";
import SelectInput from "@/UI/inputs/SelectInput";
import TextInput from "@/UI/inputs/TextInput";
// --- Logic --------------------------------------------------------------------------------------
export type TyInterestFormDetails = {
    leadId: string;
    /** The line being corrected, or `null` to write a new one. */
    interest?: TyInterest | null;
    onDone: () => void;
};

type Props = { onClose: () => void; details?: unknown };

/**
 * One line of what a person is after. Short enough to be a centred dialog rather than a panel —
 * four fields, usually two of them filled.
 *
 * The status is the field it exists for, so it is laid out as its four answers rather than hidden
 * behind a select: "they went quiet on the villa" is a one-press edit, and a menu would make it
 * three.
 */
export default function InterestFormPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { leadId, interest, onDone } = details as TyInterestFormDetails;

    const [kind, setKind] = useState<string>(interest?.kind ?? "");
    const [project, setProject] = useState(typeof interest?.project === "object" && interest.project ? interest.project._id : "");
    const [unit, setUnit] = useState(typeof interest?.unit === "object" && interest.unit ? interest.unit._id : "");
    const [status, setStatus] = useState<string>(interest?.status ?? "active");
    const [note, setNote] = useState(interest?.note ?? "");

    const projects = useFetch<{ projects: TyProject[] }>("/properties/projects", { query: { limit: constants.maxPageSize } });
    const units = useFetch<{ units: TyUnit[] }>("/properties/units", {}, false);

    const path = interest ? `/crm/leads/${leadId}/interests/${interest._id}` : `/crm/leads/${leadId}/interests`;
    const { loading, error, succeededAt, invoke } = useFetch(path, {}, false);

    // The unit list is the chosen project's — every unit in the company is a list nobody can read.
    useEffect(() => {
        if (!project) return;
        units.invoke({ query: { project, limit: constants.maxPageSize } });
    }, [project]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!succeededAt) return;
        onClose();
        onDone();
    }, [succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = () =>
        invoke({
            method: interest ? "PATCH" : "POST",
            data: { kind: kind || null, project: project || null, unit: unit || null, status, note: note.trim() || null },
        });

    const projectOptions = [
        { value: "", label: t.crm.notSet },
        ...(projects.data?.projects ?? []).map((item) => ({ value: item._id, label: readLangValue(item.name, lang) || t.crm.unnamed })),
    ];
    /**
     * Only units that actually belong to the chosen project. `units.data` still holds the previous
     * project's page for the moment between the change and the response, and an id drawn from that
     * list renders as the id itself — which is what the picker was doing.
     */
    const unitRows = (units.data?.units ?? []).filter((item) => (typeof item.project === "object" ? item.project?._id : item.project) === project);
    const unitOptions = [{ value: "", label: t.crm.notSet }, ...unitRows.map((item) => ({ value: item._id, label: item.number }))];

    return (
        <section className="side-form side-form--center">
            <header className="side-form__header">
                <h2 className="side-form__title">{interest ? t.crm.editInterest : t.crm.addInterest}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                <ChoiceInput
                    value={status}
                    onChange={(next) => setStatus(next || "active")}
                    options={constants.interestStatuses.map((item) => ({ value: item, label: t.crm.interestStatuses[item] }))}
                    label={t.crm.fields.status}
                    disabled={loading}
                />

                <SelectInput
                    value={kind}
                    onChange={setKind}
                    options={[
                        { value: "", label: t.crm.notSet },
                        ...constants.unitTypes.map((item) => ({ value: item, label: t.properties.unitTypes[item] })),
                    ]}
                    label={t.crm.fields.kind}
                    disabled={loading}
                />

                <div className="side-form__row side-form__row--pair">
                    {/* Changing the development drops the unit with it. A unit number belongs to
                        one project, so leaving the old id selected showed a raw id from a list it
                        is no longer in — and would have filed the interest against the wrong flat. */}
                    <SelectInput
                        value={project}
                        onChange={(next) => {
                            setProject(next);
                            setUnit("");
                        }}
                        options={projectOptions}
                        label={t.crm.fields.project}
                        disabled={loading}
                    />
                    {project && (
                        <SelectInput
                            value={unit}
                            onChange={setUnit}
                            options={unitOptions}
                            label={t.crm.fields.unit}
                            disabled={loading || units.loading}
                        />
                    )}
                </div>

                <TextInput value={note} onChange={setNote} label={t.crm.fields.notes} multiline rows={2} disabled={loading} />

                {error && <p className="side-form__error">{error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={loading}>
                    {loading ? t.common.loading : t.common.save}
                </button>
            </footer>
        </section>
    );
}
